import { Box, InputAdornment, debounce } from '@mui/material';
import { useEffect, useState, useCallback } from 'react';
import { Search } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { getNewParameters, getParameters } from '../../lib/helper';
import {
  AutoSuggestForExclusiveImage,
  searchImageByName,
  searchImageResult,
} from '../../Services/legal';
import CustomInput from '../CommonComp/CustomInput/CustomInput';
import CustomBox from '../CommonComp/CustomBox/CustomBox';
import MatTable from '../Tables/MatTable';

const SEARCH_LIMIT = 100;

const column = [
    {
      label: 'Image Name',
      id: 'imagename',
      format: (columnData, rowIndex, formatCallback, option, errors, row) => (
        <span
          style={{ display: 'inline-block', width: '100%', textAlign: 'left' }}
        >
          <a
            className="w-100"
            href={`https://imagesbazaar.com/moreinfo/${row?._source?.imagename}`}
            target="_blank"
            rel="noopener noreferrer"
            loading="lazy"
          >
            {row?._source?.imagename}
          </a>
        </span>
      ),
      numeric: 'center',
    },
];

function ImageLink() {
  const [tableListing, setTableListing] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [allParams, setAllParams] = useState(getParameters());
  const [isLoadingImages, setIsLoadingImages] = useState(true); // Start true
  const [searchText, setSearchText] = useState(allParams.searchKeyword || '');
  const [autoComplete, setAutoComplete] = useState([]);
  const [showAutoComplete, setShowAutoComplete] = useState(false);
  const [limit, setLimit] = useState(Number(allParams.limit) || SEARCH_LIMIT);
  const [cursor, setCursor] = useState(-1);
  const navigate = useNavigate();
  const location = useLocation();

  const getListing = useCallback(async (params = {}) => {
    const effectiveSearchText = params.searchText !== undefined ? params.searchText : searchText;
    const effectivePage = params.page || page;
    const effectiveLimit = params.limit || limit;

    if (!effectiveSearchText?.trim()) {
      setTableListing([]);
      setTotal(0);
      setIsLoadingImages(false);
      return;
    }

    setIsLoadingImages(true);
    try {
      let response = {};
      let isValid = false;
      let regex = '^(?=.*[a-zA-Z])(?=.*[0-9])[A-Za-z0-9]+$';
      let regexForStringCheck = '^[a-zA-Z][a-zA-Z][0-9]{3,}$';
      
      const searchStr = effectiveSearchText?.trim()?.split(' ');
      
      isValid = searchStr?.every(item => {
        const isImageName = item?.match(regex) && item?.match(regexForStringCheck);
        return !!isImageName;
      });

      if (isValid) {
        response = await searchImageByName(effectiveSearchText);
      } else {
        response = await searchImageResult(
          effectiveSearchText,
          effectiveLimit,
          effectivePage,
        );
      }
      if (response?.status === 200) {
        setTableListing(response?.data?.hits?.hits || []);
        setTotal(response?.data?.hits?.total?.value || 0);
      }
    } catch (error) {
      console.error("Error fetching image listing:", error);
      setTableListing([]);
      setTotal(0);
    } finally {
      setIsLoadingImages(false);
    }
  }, [searchText, page, limit]);

  useEffect(() => {
    const paramsFromUrl = getParameters();
    setAllParams(paramsFromUrl);
    setSearchText(paramsFromUrl.searchKeyword || '');
    const pageFromUrl = Number(paramsFromUrl.page) || 1;
    const limitFromUrl = Number(paramsFromUrl.limit) || SEARCH_LIMIT;
    setPage(pageFromUrl);
    setLimit(limitFromUrl);

    getListing({ 
      page: pageFromUrl, 
      limit: limitFromUrl, 
      searchText: paramsFromUrl.searchKeyword 
    });
  }, [location.search]);

  useEffect(() => {
    const data = getNewParameters(allParams);
    if (data?.search !== location.search) {
      navigate(data.newUrl, { replace: false });
    }
  }, [allParams, navigate, location.search]);

  const handleSearch = (newSearchText) => {
    const trimmedText = newSearchText.trim();
    setSearchText(trimmedText);
    setPage(1);
    setAllParams({
      ...allParams,
      searchKeyword: trimmedText,
      page: '1',
    });
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 38 && cursor > 0) {
      setCursor(cursor - 1);
    } else if (e.keyCode === 40 && cursor < autoComplete.length - 1) {
      setCursor(cursor + 1);
    }
    if (e.keyCode === 13) {
      setShowAutoComplete(false);
      const newSearchTerm = autoComplete[cursor] ? autoComplete[cursor]?.displayKeyword : searchText;
      handleSearch(newSearchTerm);
    }
  };

  const debouncedAutoComplete = useCallback(
    debounce(async (value) => {
      if (value && value.length > 0) {
        let res = await AutoSuggestForExclusiveImage(value);
        setAutoComplete(res?.rows || []);
        setShowAutoComplete(true);
      } else {
        setAutoComplete([]);
      }
    }, 300),
    []
  );

  const handleSearchTextChange = (event) => {
    const { value } = event.target;
    setSearchText(value);
    debouncedAutoComplete(value);
  };
  
  return (
    <>
      <CustomBox
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          overflow: 'visible',
          gap: '2rem',
          '@media only screen and (min-width: 280px) and (max-width: 1256px)': {
            flexDirection: 'column',
            alignItems: 'start',
            gap: '1rem',
          },
        }}
      >
        <Box
          sx={{
            paddingTop: 2,
            paddingBottom: 2,
            width: '100%',
            display: 'flex',
            maxWidth: 700,
            justifyContent: 'center',
            flexDirection: 'column',
            position: 'relative',
          }}
        >
          <Box sx={{ width: '100%' }}>
            <CustomInput
              variant="outlined"
              fullWidth
              placeholder="Search"
              sx={(theme) => ({ /* styles... */ })}
              id="searchForExclusiveImage"
              value={searchText}
              autoComplete="off"
              onFocus={() => { if (autoComplete.length > 0) setShowAutoComplete(true); }}
              onKeyDown={handleKeyDown}
              onChange={handleSearchTextChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment
                    position={'end'}
                    style={{ cursor: 'pointer', marginTop: '6px' }}
                  >
                    <span onClick={() => handleSearch(searchText)}>
                      <Search />
                    </span>
                  </InputAdornment>
                ),
              }}
            />
            {autoComplete?.length > 0 && showAutoComplete ? (
              <Box
                className="autocomplete"
                sx={{ maxWidth: 700, position: 'absolute', zIndex: 3 }}
              >
                {autoComplete?.map((item, index) => (
                  <Box
                    className={`autocomplete_inner ${
                      cursor === index ? 'active' : ''
                    }`}
                    key={item.displayKeyword} // Use a stable key
                    onClick={() => {
                      setShowAutoComplete(false);
                      handleSearch(item?.displayKeyword);
                    }}
                  >
                    {item?.displayKeyword}
                  </Box>
                ))}
              </Box>
            ) : null}
          </Box>
        </Box>
      </CustomBox>
      <div>
        <Box sx={{  }}>
          <MatTable
            loading={isLoadingImages}
            showCheckbox={false}
            columns={column}
            data={tableListing}
            page={page}
            perPage={limit}
            showPageLimit={true}
            pageLimitArray={[100, 250, 500]}
            handleChangePageLimit={(e) => {
              const newLimit = Number(e.target.value);
              setLimit(newLimit);
              setPage(1);
              setAllParams({
                ...allParams,
                page: '1',
                limit: newLimit,
              });
            }}
            total={total}
            setPage={setPage}
            handleSearch={() => {  }}
            handlePageChange={(newPage) => {
              setPage(newPage);
              setAllParams({
                ...allParams,
                page: String(newPage),
              });
            }}
            toolbarEnable={false}
            pagination={true}
            filtersUrlData={allParams || {}}
            disableSearch
          />
        </Box>
      </div>
    </>
  );
}

export default ImageLink;